import { Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap"
import i18next from "i18next"
import _ from 'lodash';
import { Formik } from "formik";
import CreatableSelect from 'react-select/creatable';
import ReactQuill from 'react-quill';
import ValidationError from '../ValidationError/ValidationError';
import { ReactSelectOption } from '../../types';
import { emailIsValid, removeDuplicates } from '../../utils';
import { MultiValue, SingleValue } from 'react-select';
import { StellantisMailDto, StellantisQuoteRequestDTO } from "src/api/models/StellantisDTO";
import * as Yup from 'yup';
import toaster from '../../utils/toaster';
import { useAuth } from "src/hooks/useAuth";

type Props = {
  isOpen?: boolean;
  closeModal?: (reload?: boolean) => void;
  doSendMail: (data: any) => void;
  quoteRequest: StellantisQuoteRequestDTO;
  loading?: boolean;
}

const emailTemplate = `
Spett.le Cliente,
<br>
con la presente le invio il preventivo per la soluzione di noleggio da lei richiesta.
In attesa di un suo gentile riscontro resto a disposizione per ogni dubbio.
<br>
Cordiali saluti
<br>
  `;

const defaultMailData = (cc: MultiValue<ReactSelectOption> = []) => ({
  to: null,
  body: emailTemplate,
  cc,
})

const validationSchema = Yup.object().shape({
  to: Yup.object().shape({
    value: Yup.string().required(i18next.t('required_field')),
    label: Yup.string().required(i18next.t('required_field')),
  })
    .required(i18next.t('required_field'))
    .test('valid-email', i18next.t('invalid_email'), (value) => {
      return value ? emailIsValid(value.value) : false;
    }),
  cc: Yup.array().of(
    Yup.object().shape({
      value: Yup.string().test('valid-email', i18next.t('invalid_email'), (value) =>
        value ? emailIsValid(value) : true
      ),
    })
  ),
  body: Yup.string().required(i18next.t('required_field')),
})

const StellantisSendQuoteMailModal: React.FC<Props> = ({ loading, quoteRequest, isOpen, doSendMail, closeModal = _.noop }) => {
  const { user } = useAuth();

  const onClose = () => {
    closeModal();
  }

  const defaultCc = user?.username ? [{ value: user.username, label: user.username }] : [];

  const handleFormSubmit = (values: any) => {
    const payload: StellantisMailDto = {
      to: values.to?.value,
      cc: values.cc?.map(item => item.value),
      body: values?.body,
      uploadedAttachments: [],
      existingAttachments: [],
    }

    const isInvalid = _.isEmpty(payload.to) || _.isEmpty(payload.body);

    if (isInvalid) {
      toaster.error(i18next.t('invalid_form'));
      return;
    }

    doSendMail(payload);
  }

  return (
    <Modal show={isOpen} size="lg" backdrop="static" keyboard={false}>
      <Modal.Title className="p-3">{i18next.t('send_quote_mail_title')}</Modal.Title>
      <Formik
        initialValues={defaultMailData(defaultCc)}
        validationSchema={validationSchema}
        onSubmit={(values) => handleFormSubmit(values)}
        enableReinitialize={true}
      >
        {({ errors, values, setFieldValue, touched, handleSubmit }) => (
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{i18next.t('to_recipient')}</Form.Label>
                    <CreatableSelect
                      isClearable
                      components={{ DropdownIndicator: null }}
                      placeholder="A"
                      noOptionsMessage={() => i18next.t('add_customer_email')}
                      value={values.to}
                      formatCreateLabel={(i) => (emailIsValid(i) ? `Invia a ${i}` : `${i}`)}
                      onChange={(newValue: SingleValue<ReactSelectOption>) => {
                        if (!newValue || emailIsValid(newValue.value)) setFieldValue('to', newValue);
                      }}
                    />
                    <ValidationError {...{ errors, touched, name: 'to' }} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{i18next.t('cc_recipients')}</Form.Label>
                    <CreatableSelect
                      isMulti
                      components={{ DropdownIndicator: null }}
                      placeholder="C.c."
                      noOptionsMessage={() => i18next.t('add_cc_recipients')}
                      value={values.cc}
                      formatCreateLabel={(i) => (emailIsValid(i) ? `Aggiungi ${i}` : `${i}`)}
                      onChange={(newValue: MultiValue<ReactSelectOption>) => {
                        const filtered: ReactSelectOption[] = removeDuplicates(
                          newValue.filter((el) => emailIsValid(el.value))
                        );
                        setFieldValue('cc', filtered);
                      }}
                    />
                    <ValidationError {...{ errors, touched, name: 'cc' }} />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mt-2">
                <Col xs={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>{i18next.t('message')}</Form.Label>
                    <ReactQuill
                      theme="snow"
                      placeholder={i18next.t('write_email')}
                      value={values.body}
                      onChange={(body) => setFieldValue('body', body)}
                    />
                    <ValidationError {...{ errors, touched, name: 'message' }} />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col xs={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>{i18next.t('attachments')}</Form.Label>
                    <div className="fw-bold">{quoteRequest?.hmpPdf?.fileName}</div>
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="link" disabled={loading} onClick={onClose}>{i18next.t('cancel')}</Button>
              <Button type="submit" disabled={loading}>
                {loading ? <Spinner animation="border" size="sm" /> : i18next.t('send_mail')}
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>

    </Modal>
  )
}

export default StellantisSendQuoteMailModal;
